import React, { useEffect, useMemo, useState } from 'react';
import {
  View,
  Text,
  TouchableOpacity,
  Modal,
  Pressable,
  StyleSheet,
  Platform,
  useWindowDimensions,
} from 'react-native';
import MaterialIcons from '@expo/vector-icons/MaterialIcons';
import { useTheme } from '../context/ThemeContext';
import {
  isoDateToLongDisplay,
  isoFromParts,
  localTodayIsoDate,
  normalizeIsoDateString,
  parseIsoToParts,
} from '../utils/dateFormat';

const WEEKDAY_LABELS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];
const LIGHT_CREAM = '#faf8f4';

function buildMonthCells(year, monthIndex) {
  const firstWeekday = new Date(year, monthIndex, 1).getDay();
  const daysInMonth = new Date(year, monthIndex + 1, 0).getDate();
  const cells = [];
  for (let i = 0; i < firstWeekday; i += 1) cells.push(null);
  for (let day = 1; day <= daysInMonth; day += 1) cells.push(day);
  while (cells.length % 7 !== 0) cells.push(null);
  return cells;
}

export default function ClassBookDatePickerModal({
  visible,
  selectedIso,
  onClose,
  onSelectDate,
}) {
  const theme = useTheme();
  const { width: windowWidth } = useWindowDimensions();
  const todayIso = localTodayIsoDate();
  const selectedKey = normalizeIsoDateString(selectedIso) || todayIso;
  const initial = parseIsoToParts(selectedKey) || parseIsoToParts(todayIso);
  const [viewYear, setViewYear] = useState(initial?.year ?? new Date().getFullYear());
  const [viewMonthIndex, setViewMonthIndex] = useState(
    initial?.monthIndex ?? new Date().getMonth()
  );

  useEffect(() => {
    if (!visible) return;
    const parts = parseIsoToParts(selectedKey) || parseIsoToParts(todayIso);
    if (parts) {
      setViewYear(parts.year);
      setViewMonthIndex(parts.monthIndex);
    }
  }, [visible, selectedKey, todayIso]);

  const cells = useMemo(
    () => buildMonthCells(viewYear, viewMonthIndex),
    [viewYear, viewMonthIndex]
  );

  const sheetWidth = Math.min(380, Math.max(280, windowWidth - 32));
  const dayCellSize = Math.floor((sheetWidth - 32) / 7);
  const gold = theme.colors.primary || '#d4af37';
  const styles = getStyles(theme, sheetWidth, dayCellSize, gold);

  const goPrevMonth = () => {
    if (viewMonthIndex === 0) {
      setViewMonthIndex(11);
      setViewYear((y) => y - 1);
      return;
    }
    setViewMonthIndex((m) => m - 1);
  };

  const goNextMonth = () => {
    if (viewMonthIndex === 11) {
      setViewMonthIndex(0);
      setViewYear((y) => y + 1);
      return;
    }
    setViewMonthIndex((m) => m + 1);
  };

  const handlePickDay = (day) => {
    if (!day) return;
    const iso = isoFromParts(viewYear, viewMonthIndex, day);
    onSelectDate?.(iso);
    onClose?.();
  };

  return (
    <Modal visible={visible} transparent animationType="fade" onRequestClose={onClose}>
      <View style={styles.modalRoot}>
        <Pressable
          style={styles.backdrop}
          onPress={onClose}
          {...(Platform.OS === 'web' ? { dataSet: { classBookDatePickerBackdrop: 'true' } } : {})}
        />
        <View style={styles.centerWrap} pointerEvents="box-none">
          <View
            style={styles.sheet}
            {...(Platform.OS === 'web' ? { dataSet: { classBookDatePickerSheet: 'true' } } : {})}
          >
            <Text style={styles.title}>Select class date</Text>
            <Text style={styles.subtitle}>{isoDateToLongDisplay(selectedKey, false)}</Text>

            <View style={styles.monthNav}>
              <TouchableOpacity onPress={goPrevMonth} style={styles.navBtn} accessibilityLabel="Previous month">
                <MaterialIcons name="chevron-left" size={28} color={theme.colors.text} />
              </TouchableOpacity>
              <Text style={styles.monthLabel}>
                {MONTH_NAMES[viewMonthIndex]} {viewYear}
              </Text>
              <TouchableOpacity onPress={goNextMonth} style={styles.navBtn} accessibilityLabel="Next month">
                <MaterialIcons name="chevron-right" size={28} color={theme.colors.text} />
              </TouchableOpacity>
            </View>

            <View style={styles.weekdayRow}>
              {WEEKDAY_LABELS.map((label) => (
                <Text key={label} style={styles.weekdayLabel}>
                  {label}
                </Text>
              ))}
            </View>

            <View style={styles.grid}>
              {cells.map((day, index) => {
                if (day == null) {
                  return <View key={`empty-${index}`} style={styles.dayCellEmpty} />;
                }
                const iso = isoFromParts(viewYear, viewMonthIndex, day);
                const isSelected = iso === selectedKey;
                const isToday = iso === todayIso;
                return (
                  <TouchableOpacity
                    key={iso}
                    style={[
                      styles.dayCell,
                      isToday && styles.dayCellToday,
                      isSelected && styles.dayCellSelected,
                    ]}
                    onPress={() => handlePickDay(day)}
                    {...(Platform.OS === 'web' ? { dataSet: { classBookDatePickerDay: 'true' } } : {})}
                  >
                    <Text
                      style={[
                        styles.dayCellText,
                        isToday && styles.dayCellTextToday,
                        isSelected && styles.dayCellTextSelected,
                      ]}
                    >
                      {day}
                    </Text>
                  </TouchableOpacity>
                );
              })}
            </View>
          </View>
        </View>
      </View>
    </Modal>
  );
}

function getStyles(theme, sheetWidth, dayCellSize, gold) {
  return StyleSheet.create({
    modalRoot: {
      flex: 1,
      backgroundColor: 'transparent',
    },
    backdrop: {
      ...StyleSheet.absoluteFillObject,
      backgroundColor: 'rgba(0,0,0,0.55)',
    },
    centerWrap: {
      flex: 1,
      justifyContent: 'center',
      alignItems: 'center',
      padding: 16,
      backgroundColor: 'transparent',
    },
    sheet: {
      width: sheetWidth,
      borderRadius: 12,
      padding: 16,
      backgroundColor: theme.isDarkMode ? theme.colors.surface : LIGHT_CREAM,
      borderWidth: 1,
      borderColor: theme.colors.border,
    },
    title: {
      fontSize: 18,
      fontWeight: '700',
      color: theme.colors.text,
      backgroundColor: 'transparent',
    },
    subtitle: {
      marginTop: 4,
      marginBottom: 12,
      fontSize: 14,
      color: theme.colors.text,
      opacity: 0.75,
      backgroundColor: 'transparent',
    },
    monthNav: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'space-between',
      marginBottom: 8,
      backgroundColor: 'transparent',
    },
    navBtn: {
      padding: 4,
      backgroundColor: 'transparent',
    },
    monthLabel: {
      fontSize: 16,
      fontWeight: '600',
      color: theme.colors.text,
      backgroundColor: 'transparent',
    },
    weekdayRow: {
      flexDirection: 'row',
      marginBottom: 4,
      backgroundColor: 'transparent',
    },
    weekdayLabel: {
      width: dayCellSize,
      textAlign: 'center',
      fontSize: 11,
      fontWeight: '600',
      color: theme.colors.text,
      opacity: 0.65,
      backgroundColor: 'transparent',
    },
    grid: {
      flexDirection: 'row',
      flexWrap: 'wrap',
      backgroundColor: 'transparent',
    },
    dayCellEmpty: {
      width: dayCellSize,
      height: dayCellSize,
      backgroundColor: 'transparent',
    },
    dayCell: {
      width: dayCellSize,
      height: dayCellSize,
      alignItems: 'center',
      justifyContent: 'center',
      borderRadius: dayCellSize / 2,
      backgroundColor: 'transparent',
    },
    dayCellToday: {
      borderWidth: 1,
      borderColor: gold,
    },
    dayCellSelected: {
      backgroundColor: gold,
    },
    dayCellText: {
      fontSize: 14,
      color: theme.colors.text,
      backgroundColor: 'transparent',
    },
    dayCellTextToday: {
      fontWeight: '700',
    },
    dayCellTextSelected: {
      color: '#111',
      fontWeight: '700',
    },
  });
}
